import React, { useEffect, useState } from "react";
import { useCheckout } from "context/CheckoutContext";
import { CLIENT_LISTING_ADDONS_OBJECT } from "data/backEndTypes";

interface AddOnItemProps {
  addon: CLIENT_LISTING_ADDONS_OBJECT;
  onUpdate: (updatedAddon: CLIENT_LISTING_ADDONS_OBJECT) => void;
}

const AddOnItem: React.FC<AddOnItemProps> = ({ addon, onUpdate }) => {
  // Read the selected flag from the addon prop.
  const selected = !!addon.selected;

  // Toggle the selection state and propagate the update.
  const toggleSelection = () => {
    onUpdate({ ...addon, selected: !selected });
  };

  return (
    <div className="flex items-center justify-between border rounded-md p-4 hover:shadow-lg transition duration-150 ease-in-out">
      <div className="flex items-center space-x-4">
        {addon.img && (
          <img
            src={addon.img}
            alt={addon.title}
            className="w-20 h-20 object-cover rounded-md"
          />
        )}
        <div>
          <h3 className="text-lg font-semibold text-gray-700">{addon.title}</h3>
          <p className="text-sm text-gray-500">{addon.description}</p>
        </div>
      </div>
      <div className="flex items-center space-x-3">
        <span className="text-lg font-medium text-gray-800">
          ${addon?.amount?.toFixed(0) || "0"}
        </span>
        <input
          type="checkbox"
          checked={selected}
          onChange={toggleSelection}
          className="h-5 w-5 text-primary-6000 focus:ring-primary-6000 border-primary-6000 rounded"
        />
      </div>
    </div>
  );
};

interface AddOnsSelectorProps {
  onChange?: (selectedAddOns: CLIENT_LISTING_ADDONS_OBJECT[]) => void;
}

const AddOnsSelector: React.FC<AddOnsSelectorProps> = ({ onChange }) => {
  const { listing, selectedAddons, setSelectedAddons, setStep } = useCheckout();

  // Base list of addons from the listing.
  const baseAddOns: CLIENT_LISTING_ADDONS_OBJECT[] = listing?.addons?.items || [];

  const [addOns, setAddOns] = useState<CLIENT_LISTING_ADDONS_OBJECT[]>(baseAddOns);

  // Sync local addOns with listing and the global selectedAddons so that selection persists.
  useEffect(() => {
    // Get the selected addons from the context.
    const selectedItems =
      (selectedAddons && (selectedAddons.addons as CLIENT_LISTING_ADDONS_OBJECT[])) || [];
    // Map through the base list and mark each addon as selected if it is present in the global selectedAddons.
    const updatedAddOns = baseAddOns.map((addon) => ({
      ...addon,
      selected: selectedItems.some((sel) => sel.id === addon.id),
    }));
    setAddOns(updatedAddOns);
  }, [listing, selectedAddons, baseAddOns]);

  // Update the global checkout context with the selected add-ons.
  const updateContext = (updatedAddOns: CLIENT_LISTING_ADDONS_OBJECT[]) => {
    setSelectedAddons((prevAddons) => ({
      ...prevAddons,
      addons: updatedAddOns,
    }));
    setStep(2);
  };

  // Handle updates coming from a child AddOnItem.
  const handleUpdate = (updatedAddon: CLIENT_LISTING_ADDONS_OBJECT) => {
    const updated = addOns.map((addon) =>
      addon.id === updatedAddon.id ? updatedAddon : addon
    );
    setAddOns(updated);

    // Filter selected addons.
    const selectedItems = updated.filter((addon) => addon.selected);
    updateContext(selectedItems);
    if (onChange) onChange(selectedItems);
  };

  if (!baseAddOns.length) return null;

  return (
    <div className="bg-white shadow-md rounded-lg p-6">
      <h2 className="text-2xl font-bold mb-6 text-gray-800">
        Enhance Your Stay
      </h2>
      <div className="space-y-4">
        {addOns.map((addon) => (
          <AddOnItem key={addon.id} addon={addon} onUpdate={handleUpdate} />
        ))}
      </div>
    </div>
  );
};

export default AddOnsSelector;